import { useMemo, useState } from "react";

import { type FetchResult } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";

import {
  AssistantScope,
  ConversationBuildDocument,
  ConversationDestroyDocument,
  ConversationMessageBuildDocument,
  ConversationReadsDocument,
  ConversationRenameDocument,
  DashboardConversationsDetailsDocument,
  ProjectAssistantChatsDocument,
} from "@app_schema";

import { useConversationStop } from "@application/hooks/use_conversation_stop";

import { track } from "@application/utilities/track";

import { nextChatAfterDelete } from "./next_chat";
import { useRefetchOnActivity } from "./use_refetch_on_activity";
import { useThinkingSeconds } from "./use_thinking_seconds";
import { appendMessage } from "@application/components/dashboard/conversations/append_message";

export type ChatReads = {
  letterIds: Set<string>;
  pages: Map<string, Set<number>>;
  readingNow: {
    documentId?: string | null;
    letterIds: string[];
    pages: number[];
    image: boolean;
  } | null;
  searchingNow: {
    query: string;
    found: number;
    letterIds?: string[];
    pages?: { documentId: string; numbers: number[] }[];
  } | null;
  filesNow: number | null;
  checkingReferences: boolean;
  // Searches and reads in our reference files that returned in this chat.
  referencesChecked: number;
  // Note saves and deletes that returned in this chat; null until loaded.
  writtenNotes: number | null;
  savedNoteNow: boolean;
  noteNow: boolean;
  // When this chat last read each note, and the note it last read or wrote.
  notesRead: Map<string, string>;
  currentNote: string | null;
  coverage: { read: number; total: number } | null;
};

// A new chat has read nothing.
const EMPTY_READS: ChatReads = {
  letterIds: new Set(),
  pages: new Map(),
  readingNow: null,
  searchingNow: null,
  filesNow: null,
  checkingReferences: false,
  referencesChecked: 0,
  writtenNotes: null,
  savedNoteNow: false,
  noteNow: false,
  notesRead: new Map(),
  currentNote: null,
  coverage: null,
};

// One scope's chats on a case: which one is shown, sending, renaming and
// deleting. `selectedId` undefined shows the newest chat; null is a new chat.
// Nothing loads while the pane is closed.
export const useAssistantChats = ({
  projectId,
  scope,
  agentId,
  open,
}: {
  projectId: string;
  scope: AssistantScope;
  agentId?: string;
  open: boolean;
}) => {
  const [selectedId, setSelectedId] = useState<string | null | undefined>();
  const [error, setError] = useState<string | null>(null);
  const { data } = useQuery(ProjectAssistantChatsDocument, {
    variables: { id: projectId, scope },
    fetchPolicy: "cache-and-network",
    skip: !open,
  });
  const chats = data?.project.assistantChats ?? [];
  const current =
    selectedId === null
      ? null
      : (chats.find((chat) => chat.id === selectedId) ?? chats[0] ?? null);

  // The shown chat's live status (the thread's own query): only it can be
  // stopped, and only its answer disables the questions.
  const { data: details } = useQuery(DashboardConversationsDetailsDocument, {
    variables: { id: current?.id ?? "" },
    skip: !open || !current,
    fetchPolicy: "cache-first",
  });
  const { running, stopping, stop } = useConversationStop(
    details?.conversation,
  );

  // What the shown chat has read, for the marks beside it. The thread refetches
  // on every live update; this follows only when a tool call starts or
  // returns, or an answer's status moves, not on every streamed word.
  const { data: readsData, refetch: refetchReads } = useQuery(
    ConversationReadsDocument,
    {
      variables: {
        id: current?.id ?? "",
        letters: scope === AssistantScope.Records,
      },
      skip: !open || !current,
    },
  );
  const readsKey = details?.conversation.messages
    .map(
      (message) =>
        `${message.id}:${message.status}:${[
          ...message.projectToolCallRequests,
          ...message.systemToolCallRequests,
        ]
          .map((request) => `${request.id}${request.result ? "+" : ""}`)
          .join(".")}`,
    )
    .join("|");
  useRefetchOnActivity(current?.id, readsKey, refetchReads);
  const reads = useMemo((): ChatReads => {
    const data = readsData?.conversation.reads;
    // What the answer in progress is doing now; nothing once it's done.
    const now = running ? data : undefined;
    return {
      letterIds: new Set(data?.letterIds),
      pages: new Map(
        (data?.pages ?? []).map((read) => [
          read.documentId,
          new Set(read.numbers),
        ]),
      ),
      readingNow: now?.readingNow ?? null,
      searchingNow: now?.searchingNow ?? null,
      filesNow: now?.filesNow ?? null,
      checkingReferences: now?.checkingReferences ?? false,
      referencesChecked: data?.referencesChecked ?? 0,
      writtenNotes: data?.writtenNotes ?? null,
      savedNoteNow: now?.savedNoteNow ?? false,
      noteNow: now?.noteNow ?? false,
      notesRead: new Map(
        (data?.notesRead ?? []).map((read) => [read.key, read.readAt]),
      ),
      currentNote: data?.currentNote ?? null,
      coverage: data?.recordsCoverage ?? data?.reportCoverage ?? null,
    };
  }, [readsData, running]);
  const thinkingSeconds = useThinkingSeconds({
    running: running && !stopping,
    seconds: running
      ? details?.conversation.messages.at(-1)?.thinkingSeconds
      : undefined,
  });

  // Sends don't wait on the list: it reorders in the background.
  const chatsQuery = {
    query: ProjectAssistantChatsDocument,
    variables: { id: projectId, scope },
  };
  const [build, { loading: building }] = useMutation(
    ConversationBuildDocument,
    {
      update(cache, { data }) {
        const built = data?.conversations.build.conversation;
        if (!built) return;
        cache.updateQuery(chatsQuery, (list) =>
          list
            ? {
                project: {
                  ...list.project,
                  assistantChats: [built, ...list.project.assistantChats],
                },
              }
            : list,
        );
      },
    },
  );
  const [chat, { loading: sending }] = useMutation(
    ConversationMessageBuildDocument,
    {
      refetchQueries: [chatsQuery],
      update: (cache, { data }, { variables }) => {
        if (variables) {
          appendMessage(cache, variables.id, data?.conversation.chat.message);
        }
      },
    },
  );
  const [renameChat] = useMutation(ConversationRenameDocument, {
    refetchQueries: [chatsQuery],
  });
  const [destroy] = useMutation(ConversationDestroyDocument, {
    refetchQueries: [chatsQuery],
  });

  const send = async ({
    message,
    fileIDs,
  }: {
    message: string;
    fileIDs?: string[];
  }): Promise<FetchResult> => {
    if (current) {
      const result = await chat({
        variables: { id: current.id, input: { text: message, fileIDs } },
      });
      if (!result.error) {
        track("assistant_message_sent", {
          conversation_id: current.id,
          project_id: projectId,
          scope,
        });
      }
      return result;
    }

    if (!agentId) return { data: undefined };

    const result = await build({
      variables: {
        input: {
          agentID: agentId,
          projectID: projectId,
          message: { text: message, fileIDs },
        },
      },
    });
    const built = result.data?.conversations.build.conversation;
    if (built) {
      setSelectedId(built.id);
      track("assistant_message_sent", { project_id: projectId, scope });
      track("assistant_consulted", { project_id: projectId, scope });
    }
    return result;
  };

  const selectChat = (id: string | null) => {
    setError(null);
    setSelectedId(id);
  };

  const rename = async (id: string, name: string) => {
    const result = await renameChat({ variables: { id, name } });
    setError(result.error ? "Couldn't rename the chat. Try again." : null);
  };

  // False when the delete failed, so the confirm stays open with the error.
  const remove = async (id: string) => {
    const result = await destroy({ variables: { id } });
    if (result.error) return false;

    setSelectedId(nextChatAfterDelete(chats, id, current?.id ?? null));
    return true;
  };

  return {
    chats,
    current,
    // Only a chat's author asks in it; a new chat is always yours.
    canAsk: current?.policy.ask ?? true,
    loaded: data !== undefined,
    questions: data?.project.suggestedQuestions ?? [],
    selectChat,
    newChat: () => selectChat(null),
    error,
    send,
    rename,
    remove,
    running,
    stopping,
    stop,
    reads: current ? reads : EMPTY_READS,
    // Moves when a tool call starts or returns, or an answer's status moves.
    activity: readsKey ?? "",
    // Moves only when a note call returns: the case's notes can't change otherwise.
    thinkingSeconds,
    busy: building || sending,
  } as const;
};

export type AssistantChats = ReturnType<typeof useAssistantChats>;
